import { memo, useCallback } from 'react';
import { Pressable } from 'react-native';
import {
  useDispatch,
  useSelector,
  markEventAsSeen,
  selectEventsByType,
} from '@bonanza-social/toolkit/state';
import { useViewDimensions } from '@bonanza-social/toolkit/hooks';
import { useNavigation, StackNavigationProp } from '@bonanza-social/navigation';
import {
  Box,
  Text,
  Image,
  BoxProps,
  ProgressBar,
  ViewStyle,
  useStyle,
  Styles,
} from '@bonanza-social/moonlight';
import { selectStatus } from '../../state';
import { openStatusProgressInfo } from '../../utils/status';
import StatusUpdateTooltip from '../StatusUpdateTooltip/StatusUpdateTooltip';
import StatusIndicator from '../StatusIndicator/StatusIndicator';
import { StatusSchema } from '../../services';
import { PROGRESS_IMAGE_OFFSET } from './StatusHeaderItem.content';
import useProgressPercent from '../../hooks/useProgressPercent';
import StatusHeaderPercent from '../StatusHeaderPercent/StatusHeaderPercent';

const staticStyles: BoxProps = {
  height: 40,
  position: 'relative',
  variant: 'default',
  width: '100%',
  backgroundColor: 'secondaryBackgroundDefault',
  borderRadius: 'radius-200',
  padding: 'space-200',
  gap: 'space-100',
  flexDirection: 'column',
  justifyContent: 'center',
  overflow: 'visible',
  zIndex: 1,
};

function StatusHeaderItem(props: Styles<ViewStyle>) {
  const dispatch = useDispatch();
  const status = useSelector(selectStatus);
  const events = useSelector(selectEventsByType);
  const pressableStyle = useStyle({ ...staticStyles, ...props });
  const navigation =
    useNavigation<StackNavigationProp<AppNavigatorParamsList>>();
  const missedEvent =
    (Boolean(events.Onboarding.length) && events.Onboarding[0]) ||
    (Boolean(events.StatusChange.length) && events.StatusChange[0]);
  const { ref, onLayout } = useViewDimensions('STATUS_HEADER_ITEM');
  const { onBarLayout, onPercentLayout, percentX } = useProgressPercent({
    totalLoyaltyPoints: status?.totalLoyaltyPoints,
    accumulatedLoyaltyPoints: status?.accumulatedLoyaltyPoints,
  });

  const handleClick = useCallback(() => {
    if (!status?.playerStatus) {
      return;
    }

    if (!missedEvent) {
      openStatusProgressInfo(status, navigation);

      return;
    }

    const type =
      missedEvent.type === 'Onboarding'
        ? 'Onboarding'
        : missedEvent.details?.statusState;

    if (!type) {
      return;
    }

    navigation.navigate(
      {
        Onboarding: 'StatusOnboarding',
        ToBePromoted: 'StatusPromotion',
        ToBeDemoted: 'StatusDemotion',
        ToBeKept: 'StatusRetention',
      }[type],
    );

    // In the future if we have multiple events from certain type
    // the oldest one is at index 0
    dispatch(markEventAsSeen(missedEvent.id));

    if (missedEvent.type === 'Onboarding' && events.StatusChange.length) {
      dispatch(markEventAsSeen(events.StatusChange[0].id));
    }
  }, [status, events, missedEvent, navigation, dispatch]);

  if (!status) {
    // TODO: Return proper skeleton
    return (
      <Box
        ref={ref}
        height="space-1000"
        variant="default"
        backgroundColor="secondaryBackgroundDefault"
        borderRadius="radius-200"
        position="relative"
        onLayout={onLayout}
        {...props}
      />
    );
  }

  const {
    playerStatus,
    claimedBoxesCount,
    totalLoyaltyPoints,
    accumulatedLoyaltyPoints,
  } = status;

  return (
    <Pressable
      testID="status-header-item"
      ref={ref}
      style={pressableStyle}
      onLayout={onLayout}
      onPress={handleClick}
    >
      <Text
        locNS="status"
        locKey={playerStatus}
        variant="nav-label"
        numberOfLines={1}
        gradient={`darkSurfaceStatus${playerStatus}`}
        alignSelf="flex-start"
      />
      <Box flexDirection="row">
        <ProgressBar
          flex={1}
          // make sure the progress bar keeps moving to the right
          level={Number(
            `${StatusSchema.options.indexOf(playerStatus)}${claimedBoxesCount}`,
          )}
          from={0}
          to={totalLoyaltyPoints}
          progress={accumulatedLoyaltyPoints}
          gradientType={`status${playerStatus}Background`}
          marginRight="space-200"
          width="auto"
          onLayout={onBarLayout}
          progressGlowColor={`darkSurfaceSolidStatus${playerStatus}`}
          renderPercent={(percent) => (
            <StatusHeaderPercent
              percent={percent}
              onPercentLayout={onPercentLayout}
              percentX={percentX}
            />
          )}
        />
        <Box width="space-400" />
      </Box>
      <Box position="absolute" right="space-050" justifyContent="center">
        <Image
          alignSelf="flex-end"
          position="absolute"
          name={`status.${playerStatus}Gem`}
          cachePolicy="disk"
          height={30}
          aspectRatio={1}
        />
      </Box>
      <StatusUpdateTooltip status={status} />
      <StatusIndicator visible={Boolean(missedEvent)} />
    </Pressable>
  );
}

export default memo(StatusHeaderItem);
